import { useRef, useState, useMemo } from 'react';
import {
  ImagePlus,
  RotateCw,
  Trash2,
  Loader2,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Maximize2,
  MapPin,
} from 'lucide-react';
import L from 'leaflet';
import { cn } from '../../lib/utils';
import {
  type EventMapOverlaySavePayload,
  readImageFileAsDataUrl,
  offsetOverlayCenter,
  overlayMetaFitToMapView,
  overlayScaleLimitsForMap,
  DEFAULT_OVERLAY_OPACITY,
} from '../../lib/mapOverlay';

interface MapOverlayControlsProps {
  overlay: (EventMapOverlaySavePayload & { imageUrl?: string }) | null;
  onChange: (overlay: EventMapOverlaySavePayload | null) => void;
  defaultCenter?: [number, number];
  getMap?: () => L.Map | null;
  compact?: boolean;
  saving?: boolean;
  className?: string;
}

const NUDGE_STEPS = [1, 5, 10] as const;

function resolveUploadCenter(
  getMap: (() => L.Map | null) | undefined,
  defaultCenter?: [number, number],
  existing?: EventMapOverlaySavePayload | null
): { lat: number; lng: number } | null {
  if (existing?.centerLat != null && existing?.centerLng != null && Math.abs(existing.centerLat) > 0.01) {
    return { lat: existing.centerLat, lng: existing.centerLng };
  }
  if (defaultCenter) {
    return { lat: defaultCenter[0], lng: defaultCenter[1] };
  }
  const map = getMap?.();
  if (map) {
    const c = map.getCenter();
    return { lat: c.lat, lng: c.lng };
  }
  return null;
}

export default function MapOverlayControls({
  overlay,
  onChange,
  defaultCenter,
  getMap,
  compact = false,
  saving = false,
  className,
}: MapOverlayControlsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [nudgeStep, setNudgeStep] = useState<number>(1);
  const imageSizeRef = useRef<{ width: number; height: number } | null>(null);

  const scaleLimits = useMemo(() => {
    const map = getMap?.();
    if (!map) return { min: 10, max: 2000 };
    return overlayScaleLimitsForMap(map);
  }, [getMap, overlay?.widthMeters]);

  const ensureImageSize = async (): Promise<{ width: number; height: number }> => {
    if (imageSizeRef.current) return imageSizeRef.current;
    const src = overlay?.imageDataUrl || overlay?.imageUrl;
    if (!src) {
      return {
        width: Math.max(100, overlay?.widthMeters || 1000),
        height: Math.max(100, overlay?.heightMeters || 1000),
      };
    }
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        imageSizeRef.current = { width: img.naturalWidth, height: img.naturalHeight };
        resolve(imageSizeRef.current);
      };
      img.onerror = () =>
        resolve({
          width: Math.max(100, overlay?.widthMeters || 1000),
          height: Math.max(100, overlay?.heightMeters || 1000),
        });
      img.src = src;
    });
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;

    const { dataUrl, width, height } = await readImageFileAsDataUrl(file);
    imageSizeRef.current = { width, height };

    const map = getMap?.();
    if (map) {
      const fitted = overlayMetaFitToMapView(map, width, height);
      onChange({ ...fitted, imageDataUrl: dataUrl });
      return;
    }

    const center = resolveUploadCenter(getMap, defaultCenter, overlay);
    if (!center) return;

    onChange({
      centerLat: center.lat,
      centerLng: center.lng,
      widthMeters: 200,
      heightMeters: 200 * (height / width),
      rotation: 0,
      opacity: DEFAULT_OVERLAY_OPACITY,
      imageDataUrl: dataUrl,
    });
  };

  const patch = (partial: Partial<EventMapOverlaySavePayload>) => {
    if (!overlay) return;
    onChange({ ...overlay, ...partial });
  };

  const nudge = (eastMeters: number, northMeters: number) => {
    if (!overlay) return;
    const next = offsetOverlayCenter(overlay.centerLat, overlay.centerLng, eastMeters, northMeters);
    patch(next);
  };

  const fitToMapView = async () => {
    const map = getMap?.();
    if (!map || !overlay?.imageDataUrl && !overlay?.imageUrl) return;
    const size = await ensureImageSize();
    const fitted = overlayMetaFitToMapView(map, size.width, size.height);
    onChange({
      ...overlay,
      ...fitted,
      rotation: overlay.rotation,
      opacity: overlay.opacity,
      ...(overlay.imageDataUrl ? { imageDataUrl: overlay.imageDataUrl } : {}),
    });
  };

  const centreOnPin = () => {
    if (!overlay || !defaultCenter) return;
    patch({ centerLat: defaultCenter[0], centerLng: defaultCenter[1] });
  };

  const completeOverlay = async () => {
    const map = getMap?.();
    if (!map || (!overlay?.imageDataUrl && !overlay?.imageUrl)) return;
    const size = await ensureImageSize();
    const fitted = overlayMetaFitToMapView(map, size.width, size.height);
    const center = defaultCenter
      ? { centerLat: defaultCenter[0], centerLng: defaultCenter[1] }
      : { centerLat: fitted.centerLat, centerLng: fitted.centerLng };
    onChange({
      ...overlay,
      ...fitted,
      ...center,
      rotation: overlay.rotation ?? 0,
      opacity: overlay.opacity ?? DEFAULT_OVERLAY_OPACITY,
      ...(overlay.imageDataUrl ? { imageDataUrl: overlay.imageDataUrl } : {}),
    });
  };

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
          disabled={saving}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={saving}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-accent text-foreground text-[10px] font-semibold hover:bg-accent/80 transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImagePlus className="w-3 h-3" />}
          {overlay ? 'Replace site map' : 'Upload site map'}
        </button>

        {overlay && (
          <>
            <button
              type="button"
              onClick={() => void fitToMapView()}
              disabled={saving || !getMap}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[10px] font-semibold border border-border hover:bg-accent transition-colors disabled:opacity-50"
              title="Resize overlay to match current map view"
            >
              <Maximize2 className="w-3 h-3" />
              Fit to map
            </button>
            {defaultCenter && (
              <button
                type="button"
                onClick={centreOnPin}
                disabled={saving}
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[10px] font-semibold border border-border hover:bg-accent transition-colors disabled:opacity-50"
                title="Centre overlay on event location pin"
              >
                <MapPin className="w-3 h-3" />
                Centre on pin
              </button>
            )}
            <button
              type="button"
              onClick={() => void completeOverlay()}
              disabled={saving || !getMap}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[10px] font-semibold bg-primary/15 text-primary border border-primary/25 hover:bg-primary/25 transition-colors disabled:opacity-50"
              title="Fit to map view and centre on event pin"
            >
              Complete overlay
            </button>
            <button
              type="button"
              onClick={() => onChange(null)}
              disabled={saving}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[10px] font-semibold text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
            >
              <Trash2 className="w-3 h-3" />
              Remove
            </button>
          </>
        )}
      </div>

      {overlay && (
        <>
          <div className={cn('grid gap-2', compact ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-3')}>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] text-muted-foreground">Transparency</span>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={Math.round(overlay.opacity * 100)}
                  onChange={(e) => patch({ opacity: Number(e.target.value) / 100 })}
                  disabled={saving}
                  className="flex-1 h-1 accent-primary"
                />
                <span className="text-[10px] font-mono text-foreground w-8">
                  {Math.round(overlay.opacity * 100)}%
                </span>
              </div>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                <RotateCw className="w-3 h-3" />
                Rotation
              </span>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={-180}
                  max={180}
                  step={1}
                  value={Math.round(overlay.rotation)}
                  onChange={(e) => patch({ rotation: Number(e.target.value) })}
                  disabled={saving}
                  className="flex-1 h-1 accent-primary"
                />
                <span className="text-[10px] font-mono text-foreground w-10">
                  {Math.round(overlay.rotation)}°
                </span>
              </div>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-[10px] text-muted-foreground">Scale (width)</span>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={scaleLimits.min}
                  max={scaleLimits.max}
                  step={Math.max(1, Math.round(scaleLimits.max / 200))}
                  value={Math.min(scaleLimits.max, Math.max(scaleLimits.min, Math.round(overlay.widthMeters)))}
                  onChange={(e) => {
                    const widthMeters = Number(e.target.value);
                    const aspect = overlay.heightMeters / overlay.widthMeters;
                    patch({ widthMeters, heightMeters: widthMeters * aspect });
                  }}
                  disabled={saving}
                  className="flex-1 h-1 accent-primary"
                />
                <span className="text-[10px] font-mono text-foreground w-12">
                  {Math.round(overlay.widthMeters)}m
                </span>
              </div>
            </label>
          </div>

          <div className="rounded-lg border border-border bg-muted/20 p-2 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-semibold text-foreground">Fine position (X / Y)</span>
              <div className="flex items-center gap-1">
                {NUDGE_STEPS.map((step) => (
                  <button
                    key={step}
                    type="button"
                    onClick={() => setNudgeStep(step)}
                    className={cn(
                      'px-1.5 py-0.5 rounded text-[9px] font-mono border transition-colors',
                      nudgeStep === step
                        ? 'border-primary text-primary bg-primary/10'
                        : 'border-border text-muted-foreground hover:bg-accent'
                    )}
                  >
                    {step}m
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[9px] text-muted-foreground leading-snug">
              Small nudges only — use Fit to map first, then fine-tune. East/west = X, north/south = Y.
            </p>
            <div className="flex items-center justify-center gap-1">
              <button
                type="button"
                disabled={saving}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  nudge(-nudgeStep, 0);
                }}
                className="p-2 rounded-lg border border-border hover:bg-accent transition-colors disabled:opacity-50"
                title="Move west"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
              </button>
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  disabled={saving}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    nudge(0, nudgeStep);
                  }}
                  className="p-2 rounded-lg border border-border hover:bg-accent transition-colors disabled:opacity-50"
                  title="Move north"
                >
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    nudge(0, -nudgeStep);
                  }}
                  className="p-2 rounded-lg border border-border hover:bg-accent transition-colors disabled:opacity-50"
                  title="Move south"
                >
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
              </div>
              <button
                type="button"
                disabled={saving}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  nudge(nudgeStep, 0);
                }}
                className="p-2 rounded-lg border border-border hover:bg-accent transition-colors disabled:opacity-50"
                title="Move east"
              >
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </>
      )}

      {!overlay && (
        <p className="text-[10px] text-muted-foreground">
          Upload a site plan — it auto-sizes to the current map view. Use Complete overlay to fit and
          centre on the event pin, then fine-tune transparency and position.
        </p>
      )}
    </div>
  );
}
